/**
 * Local MCP server for Sibrik video editing.
 *
 * Runs as a stdio subprocess of Claude Desktop.
 * Provides tools for:
 *   - Starting/stopping Remotion Studio (live preview in browser)
 *   - Downloading media from R2 URLs
 *   - Creating/editing Remotion compositions (video clips, text, audio)
 *   - Rendering MP4 locally
 *
 * The project state persists in a temp directory so the user can
 * close and reopen Claude Desktop without losing their work.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { ProjectManager } from './remotion/project.js';
import { renderVideo, startStudio, type RenderProgress } from './remotion/render.js';
import { downloadMedia, probeVideoDuration, probeVideoFps } from './media.js';
import path from 'node:path';
import os from 'node:os';
import { promises as fs } from 'node:fs';

// Singleton project — one active project per Claude Desktop session
let project: ProjectManager | null = null;
let studioProcess: ReturnType<typeof spawn> | null = null;
let studioUrl: string | null = null;

import { spawn, exec } from 'node:child_process';

// Version check — cached after first call
let updateInfo: { current: string; latest: string; updateAvailable: boolean } | null = null;

async function checkForUpdates(): Promise<{ current: string; latest: string; updateAvailable: boolean } | null> {
  if (updateInfo !== null) return updateInfo;

  try {
    const current = '1.0.10'; // must match package.json version

    const latest = await new Promise<string>((resolve, reject) => {
      const child = exec('npm view @sibrik/editor version', {
        timeout: 10_000,
      });
      let output = '';
      child.stdout?.on('data', (data: Buffer) => (output += data.toString()));
      child.on('close', (code) => {
        if (code === 0) resolve(output.trim());
        else reject(new Error(`npm view failed with code ${code}`));
      });
      child.on('error', reject);
    });

    updateInfo = {
      current,
      latest,
      updateAvailable: latest !== current,
    };
  } catch {
    updateInfo = { current: '1.0.10', latest: 'unknown', updateAvailable: false };
  }

  return updateInfo;
}

function updateWarning(): { message: string; command: string } | undefined {
  if (updateInfo?.updateAvailable) {
    return {
      message: `An update is available for @sibrik/editor: ${updateInfo.current} → ${updateInfo.latest}. Offer to run the update command below so the user can approve it. After updating, the user must restart their MCP client.`,
      command: `npm install -g @sibrik/editor@latest`,
    };
  }
  return undefined;
}

async function ensureProject(): Promise<ProjectManager> {
  if (project) return project;

  // Try to find an existing project in the default location
  const defaultDir = path.join(os.tmpdir(), 'sibrik-editor-project');
  project = new ProjectManager(defaultDir);

  const loaded = await project.load();
  if (!loaded) {
    await project.init();
  }

  // Check for updates on first project load
  checkForUpdates();

  return project;
}

type ToolResult = {
  content: Array<{ type: 'text'; text: string }>;
  isError?: boolean;
};

function ok(payload: unknown): ToolResult {
  return { content: [{ type: 'text', text: JSON.stringify(payload, null, 2) }] };
}

function fail(message: string): ToolResult {
  return { content: [{ type: 'text', text: message }], isError: true };
}

export function createEditorServer(): McpServer {
  const server = new McpServer({ name: 'sibrik-editor', version: '1.0.0' });

  // ─── start_studio ──────────────────────────────────────────────
  server.tool(
    'start_studio',
    'Launch Remotion Studio in the browser — gives the user a live preview of their video composition with a visual timeline. Returns the localhost URL. The studio stays running until stop_studio is called or Claude Desktop is closed.',
    {
      port: z.number().int().min(1024).max(65535).optional().describe('Port for the studio (default 3000)'),
    },
    async ({ port }) => {
      try {
        const proj = await ensureProject();
        if (studioProcess) {
          return ok({ url: studioUrl, note: 'Studio is already running.' });
        }
        const result = startStudio(proj, port ?? 3000);
        studioProcess = result.process;
        studioUrl = result.url;

        result.process.on('exit', () => {
          studioProcess = null;
          studioUrl = null;
        });

        // Give the studio a moment to start
        await new Promise((resolve) => setTimeout(resolve, 2000));

        return ok({
          url: studioUrl,
          note: 'Remotion Studio is running. Open the URL in your browser to see the live preview. You can edit visually in the studio or ask Claude to add clips, text, and audio.',
          update_warning: updateWarning(),
        });
      } catch (err) {
        return fail(`Failed to start studio: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── stop_studio ───────────────────────────────────────────────
  server.tool(
    'stop_studio',
    'Stop the running Remotion Studio process.',
    {},
    async () => {
      if (studioProcess) {
        try {
          // Kill the entire process group (negative PID)
          // This ensures child processes (remotion, esbuild) are also killed
          process.kill(-studioProcess.pid!, 'SIGTERM');
        } catch {
          // Fallback: kill just the parent process
          studioProcess.kill();
        }
        studioProcess = null;
        studioUrl = null;
        return ok({ stopped: true });
      }
      return ok({ stopped: false, note: 'Studio was not running.' });
    },
  );

  // ─── download_media ────────────────────────────────────────────
  server.tool(
    'download_media',
    'Download a media file (video, audio, or image) from a URL to the local project so it can be used in the composition. Use this to bring in videos generated through the Sibrik cloud MCP (pass the result URL from generate_video / generate_audio / generate_image). Returns the local path and metadata.',
    {
      url: z.string().url().describe('Public URL of the media file to download (e.g. R2/CDN URL from a Sibrik generation)'),
    },
    async ({ url }) => {
      try {
        const proj = await ensureProject();
        const media = await downloadMedia(proj, url);
        return ok({
          filename: media.filename,
          relative_path: media.relativePath,
          absolute_path: media.absolutePath,
          size_bytes: media.sizeBytes,
          mime_type: media.mimeType,
          note: 'Media downloaded. Use add_video_clip or add_audio_track to add it to the composition.',
        });
      } catch (err) {
        return fail(`Download failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── list_local_media ──────────────────────────────────────────
  server.tool(
    'list_local_media',
    'List all media files that have been downloaded to the local project. Returns filenames, paths, and file sizes.',
    {},
    async () => {
      try {
        const proj = await ensureProject();
        const media = await proj.listMedia();
        return ok({
          media: media,
          count: media.length,
          note: media.length === 0
            ? 'No media downloaded yet. Use download_media with a URL from your Sibrik generations.'
            : undefined,
        });
      } catch (err) {
        return fail(`Failed to list media: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── add_video_clip ────────────────────────────────────────────
  server.tool(
    'add_video_clip',
    'Add a video clip (or image) to the composition. The clip is placed at the end of the timeline by default, or at a specified start frame. You must download the video first using download_media. NOTE: the first video clip auto-detects the source FPS and sets the composition FPS to match (e.g. 24fps video → project becomes 24fps). For images, if duration_frames is omitted a default of 3 seconds is used.',
    {
      relative_path: z.string().describe('Relative path from public/ folder (e.g. "media/media-123-abc.mp4") — returned by download_media'),
      duration_frames: z.number().int().positive().optional().describe('Duration in frames. If omitted, auto-detected from the video file.'),
      start_frame: z.number().int().min(0).optional().describe('Start frame (default: end of current timeline)'),
    },
    async ({ relative_path, duration_frames, start_frame }) => {
      try {
        const proj = await ensureProject();
        const fullPath = path.join(proj.dir, 'public', relative_path);

        const isImage = /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i.test(relative_path);

        // Auto-detect source FPS and set composition FPS on first video clip (not image)
        if (!isImage && proj.compositionState.clips.length === 0) {
          const sourceFps = await probeVideoFps(fullPath);
          if (sourceFps && Math.abs(sourceFps - proj.compositionState.fps) > 0.5) {
            await proj.setFps(Math.round(sourceFps));
          }
        }

        let duration = duration_frames;
        if (!duration) {
          if (isImage) {
            // Default 3 seconds for images
            duration = Math.round(3 * proj.compositionState.fps);
          } else {
            const seconds = await probeVideoDuration(fullPath);
            if (seconds) {
              duration = Math.round(seconds * proj.compositionState.fps);
            } else {
              return fail('Could not auto-detect video duration. Please provide duration_frames explicitly.');
            }
          }
        }

        const clip = await proj.addVideoClip(relative_path, duration, start_frame);
        return ok({
          clip,
          total_duration_frames: proj.compositionState.durationFrames,
          total_duration_seconds: Math.round((proj.compositionState.durationFrames / proj.compositionState.fps) * 10) / 10,
          note: 'Clip added. If the studio is running, the preview will update automatically.',
        });
      } catch (err) {
        return fail(`Failed to add clip: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── add_text_layer ────────────────────────────────────────────
  server.tool(
    'add_text_layer',
    'Add a text overlay to the composition. The text appears at a specified time with optional fade-in animation.',
    {
      text: z.string().min(1).describe('Text content to display'),
      start_frame: z.number().int().min(0).optional().describe('Start frame (default: 0)'),
      duration_frames: z.number().int().positive().optional().describe('Duration in frames (default: 3 seconds = 90 frames at 30fps)'),
      font_size: z.number().int().min(12).max(400).optional().describe('Font size in pixels (default: 88)'),
      color: z.string().optional().describe('Text color (default: "white")'),
      position: z.enum(['top', 'center', 'bottom']).optional().describe('Vertical position (default: "center")'),
    },
    async ({ text, start_frame, duration_frames, font_size, color, position }) => {
      try {
        const proj = await ensureProject();
        const layer = await proj.addTextLayer({
          text,
          startFrame: start_frame,
          durationFrames: duration_frames,
          fontSize: font_size,
          color,
          position,
        });
        return ok({
          layer,
          note: 'Text layer added. If the studio is running, the preview will update automatically.',
        });
      } catch (err) {
        return fail(`Failed to add text: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── add_audio_track ───────────────────────────────────────────
  server.tool(
    'add_audio_track',
    'Add an audio track to the composition. You must download the audio first using download_media.',
    {
      relative_path: z.string().describe('Relative path from public/ folder (e.g. "media/media-123-abc.mp3") — returned by download_media'),
      duration_frames: z.number().int().positive().optional().describe('Duration in frames. If omitted, auto-detected from the audio file.'),
      start_frame: z.number().int().min(0).optional().describe('Start frame (default: 0)'),
      volume: z.number().min(0).max(1).optional().describe('Volume 0-1 (default: 1)'),
    },
    async ({ relative_path, duration_frames, start_frame, volume }) => {
      try {
        const proj = await ensureProject();
        const fullPath = path.join(proj.dir, 'public', relative_path);

        let duration = duration_frames;
        if (!duration) {
          const seconds = await probeVideoDuration(fullPath);
          if (seconds) {
            duration = Math.round(seconds * proj.compositionState.fps);
          } else {
            return fail('Could not auto-detect audio duration. Please provide duration_frames explicitly.');
          }
        }

        const track = await proj.addAudioTrack(relative_path, duration, start_frame, volume);
        return ok({
          track,
          note: 'Audio track added. If the studio is running, the preview will update automatically.',
        });
      } catch (err) {
        return fail(`Failed to add audio: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── remove_clip ───────────────────────────────────────────────
  server.tool(
    'remove_clip',
    'Remove a video clip from the composition by its index (0-based). Use get_project_info to see the list of clips with their indices.',
    {
      index: z.number().int().min(0).describe('Index of the clip to remove (0-based)'),
    },
    async ({ index }) => {
      try {
        const proj = await ensureProject();
        if (index >= proj.compositionState.clips.length) {
          return fail(`Invalid index ${index}. There are ${proj.compositionState.clips.length} clips.`);
        }
        await proj.removeVideoClip(index);
        return ok({ removed: true, remaining_clips: proj.compositionState.clips.length });
      } catch (err) {
        return fail(`Failed to remove clip: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── remove_text_layer ─────────────────────────────────────────
  server.tool(
    'remove_text_layer',
    'Remove a text layer from the composition by its index (0-based).',
    {
      index: z.number().int().min(0).describe('Index of the text layer to remove (0-based)'),
    },
    async ({ index }) => {
      try {
        const proj = await ensureProject();
        if (index >= proj.compositionState.textLayers.length) {
          return fail(`Invalid index ${index}. There are ${proj.compositionState.textLayers.length} text layers.`);
        }
        await proj.removeTextLayer(index);
        return ok({ removed: true, remaining_text_layers: proj.compositionState.textLayers.length });
      } catch (err) {
        return fail(`Failed to remove text layer: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── remove_audio_track ────────────────────────────────────────
  server.tool(
    'remove_audio_track',
    'Remove an audio track from the composition by its index (0-based).',
    {
      index: z.number().int().min(0).describe('Index of the audio track to remove (0-based)'),
    },
    async ({ index }) => {
      try {
        const proj = await ensureProject();
        if (index >= proj.compositionState.audioTracks.length) {
          return fail(`Invalid index ${index}. There are ${proj.compositionState.audioTracks.length} audio tracks.`);
        }
        await proj.removeAudioTrack(index);
        return ok({ removed: true, remaining_audio_tracks: proj.compositionState.audioTracks.length });
      } catch (err) {
        return fail(`Failed to remove audio track: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── set_resolution ────────────────────────────────────────────
  server.tool(
    'set_resolution',
    'Set the output video resolution. Common presets: 1920x1080 (Full HD), 1280x720 (HD), 1080x1920 (Vertical/Reels), 3840x2160 (4K).',
    {
      width: z.number().int().min(320).max(3840).describe('Width in pixels'),
      height: z.number().int().min(320).max(2160).describe('Height in pixels'),
    },
    async ({ width, height }) => {
      try {
        const proj = await ensureProject();
        await proj.setResolution(width, height);
        return ok({
          width,
          height,
          note: 'Resolution updated. Common presets: 1920x1080 (Full HD), 1080x1920 (Reels/TikTok), 1280x720 (HD).',
        });
      } catch (err) {
        return fail(`Failed to set resolution: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── set_fps ───────────────────────────────────────────────────
  server.tool(
    'set_fps',
    'Set the composition frame rate (FPS). Use this to match the FPS of your source videos (commonly 24, 25, or 30). This affects how duration_frames are calculated from seconds. Must be set BEFORE adding clips for correct frame counts.',
    {
      fps: z.number().int().min(1).max(120).describe('Frames per second (e.g. 24, 25, 30, 60)'),
    },
    async ({ fps }) => {
      try {
        const proj = await ensureProject();
        await proj.setFps(fps);
        return ok({
          fps,
          note: `FPS set to ${fps}. All subsequent clip duration calculations will use this frame rate.`,
        });
      } catch (err) {
        return fail(`Failed to set FPS: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── render_video ──────────────────────────────────────────────
  server.tool(
    'render_video',
    'Render the current composition to an MP4 file. This runs locally on your machine using Remotion + headless Chromium. The first render may take longer as Chromium needs to be downloaded. Returns the path to the rendered MP4.',
    {
      output_path: z.string().optional().describe('Custom output path for the MP4 file. If omitted, saves to the project out/ folder.'),
    },
    async ({ output_path }) => {
      try {
        const proj = await ensureProject();

        if (proj.compositionState.clips.length === 0) {
          return fail('No video clips in the composition. Use add_video_clip to add some first.');
        }

        const result = await renderVideo(proj, output_path);

        return ok({
          output_path: result.outputPath,
          render_duration_seconds: Math.round(result.durationSeconds * 10) / 10,
          composition: {
            fps: proj.compositionState.fps,
            width: proj.compositionState.width,
            height: proj.compositionState.height,
            duration_frames: proj.compositionState.durationFrames,
            duration_seconds: Math.round((proj.compositionState.durationFrames / proj.compositionState.fps) * 10) / 10,
            clips: proj.compositionState.clips.length,
            text_layers: proj.compositionState.textLayers.length,
            audio_tracks: proj.compositionState.audioTracks.length,
          },
          note: 'Video rendered successfully! The MP4 is on your local disk at the output_path.',
        });
      } catch (err) {
        return fail(`Render failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── get_project_info ──────────────────────────────────────────
  server.tool(
    'get_project_info',
    'Get the current state of the video project — all clips, text layers, audio tracks, resolution, FPS, and duration. Use this to understand what is currently in the composition before making changes.',
    {},
    async () => {
      try {
        const proj = await ensureProject();
        const info = proj.getInfo();
        return ok({
          ...info,
          studio_running: studioProcess !== null,
          studio_url: studioUrl,
          update_warning: updateWarning(),
        });
      } catch (err) {
        return fail(`Failed to get project info: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  // ─── clear_project ─────────────────────────────────────────────
  server.tool(
    'clear_project',
    'Clear all clips, text layers, and audio tracks from the composition. Starts fresh. Downloaded media files are kept on disk. NOTE: the project state persists across Claude Desktop sessions (stored in a temp directory). Call this tool at the start of a new editing session to avoid inheriting clips from a previous session.',
    {},
    async () => {
      try {
        const proj = await ensureProject();
        // Reinitialize with empty state
        const defaultDir = path.join(os.tmpdir(), 'sibrik-editor-project');
        project = new ProjectManager(defaultDir);
        await project.init();
        return ok({ cleared: true, note: 'Project cleared. All media files are still on disk.' });
      } catch (err) {
        return fail(`Failed to clear project: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  );

  return server;
}
